import { create } from 'zustand'

import { login, logout, validateSession } from '@/api/apis'
import { setSession } from '@/services/jwt.service'

/**
 * Authentication state for the whole app.
 *
 * `tokenLoading` covers the cold-start check performed by <Auth>: it starts true
 * so no route renders — and no redirect fires — before a stored token has been
 * validated. `authLoading` covers an interactive sign-in.
 */
export const useAuthStore = create((set, get) => ({
  user: null,
  authLoading: false,
  tokenLoading: true,
  authError: null,

  setUser: (user) => set({ user }),

  logoutService: async ({ callApi = false } = {}) => {
    if (callApi) await logout().catch(() => null)
    setSession(null)
    set({ user: null, authLoading: false, tokenLoading: false, authError: null })
  },

  loginService: async (credentials) => {
    set({ authLoading: true, authError: null })
    try {
      const result = await login(credentials)
      const accessToken = result?.access_token ?? result?.token

      if (!result?.user || !accessToken) {
        set({ authLoading: false, user: null, authError: 'Unexpected response from the server.' })
        return false
      }

      setSession(accessToken)
      set({ user: result.user, authLoading: false })
      return true
    } catch (error) {
      set({ authLoading: false, user: null, authError: error.message })
      return false
    }
  },

  /** Confirms a persisted token against the API and refreshes the stored user. */
  loginWithToken: async () => {
    try {
      const result = await validateSession()
      const accessToken = result?.access_token ?? result?.token

      if (!result?.user) {
        get().logoutService()
        return
      }

      if (accessToken) setSession(accessToken)
      set({ user: result.user, tokenLoading: false })
    } catch {
      get().logoutService()
    }
  },
}))

export const selectIsAuthenticated = (state) => Boolean(state.user)
